/* OrgsComboBoxComponent.js */

"use strict";

const BaseMultipleOptionSelectionControlComponent = require("@gluenetworks/input_base_control")
    .OptionSelection.Multiple;
const Renderer = require("./OrgsComboBoxRenderer");
const _ = require("lodash");

class OrgsComboBoxComponent extends BaseMultipleOptionSelectionControlComponent {
    /**
     * @schema.name Multiple Combo Box Control
     * @schema.package @gluenetworks/combobox
     * @schema.packageIdentifier Multiple
     * @schema.namespace require("@gluenetworks/steve_ui_core").Control.OptionSelection.Multiple.ComboBox
     * @schema.events ["Placeholder", "Autoselect", "DropUp", "ShowFilter", "Options", "Disabled", "Value", "Visible", "Invalid", "DarkTheme", "Focus", "SetAttribute", "Blur", "UserValue", "UserFocus", "UserBlur", "UserKeyDown"]
     *
     * @schema.configuration.placeholder.name placeholder
     * @schema.configuration.placeholder.type string
     * @schema.configuration.placeholder.default ""
     * @schema.configuration.placeholder.required false
     *
     * @schema.configuration.autoselect.name autoselect
     * @schema.configuration.autoselect.type boolean
     * @schema.configuration.autoselect.default false
     * @schema.configuration.autoselect.required false
     *
     * @schema.configuration.dropUp.name dropUp
     * @schema.configuration.dropUp.type boolean
     * @schema.configuration.dropUp.default false
     * @schema.configuration.dropUp.required false
     *
     * @schema.configuration.showFilter.name showFilter
     * @schema.configuration.showFilter.type boolean
     * @schema.configuration.showFilter.default true
     * @schema.configuration.showFilter.required false
     *
     * @schema.configuration.allowClear.name allowClear
     * @schema.configuration.allowClear.type boolean
     * @schema.configuration.allowClear.default false
     * @schema.configuration.allowClear.required false
     *
     * @schema.configuration.options.example [{ label: 'Number 1', value: { valueProp1: 'blah1', valueProp2: { somethin: 'somethin1' } }, disabled: true },{ label: 'Number 2', value: { valueProp1: 'blah2', valueProp2: { somethin: 'somethin2' } } },{ label: 'Number 3', value: { valueProp1: 'blah3', valueProp2: { somethin: 'somethin3' } }, disabled: true },{ label: 'Number 4', value: { valueProp1: 'blah4', valueProp2: { somethin: 'somethin4' } } },{ label: 'Number 5', value: { valueProp1: 'blah5', valueProp2: { somethin: 'somethin5' } } }]
     *
     * @schema.configuration.value.example [{ valueProp1: 'blah1', valueProp2: { somethin: 'somethin1' } },{ valueProp1: 'blah2', valueProp2: { somethin: 'somethin2' } }]
     *
     */

    constructor(configOptions) {
        super(configOptions);

        this._DEFAULT_PLACEHOLDER = "";

        this._multiple = true;
        this._placeholder = _.get(configOptions, "placeholder", this._DEFAULT_PLACEHOLDER);
        this._autoselect = _.get(configOptions, "autoselect", false);
        this._dropUp = _.get(configOptions, "dropUp", false);
        this._showFilter = _.get(configOptions, "showFilter", true);
        this._allowClear = _.get(configOptions, "allowClear", false);

        this._renderer = new Renderer(this);

        this._registerListeners();
    }
    /*******************************************/
    /* START PRIVATE COMPONENT MODEL METHODS */
    /*******************************************/
    _registerListeners() {}
    _unregisterListeners() {}
    /*******************************************/
    /* END PRIVATE COMPONENT MODEL METHODS */
    /* START PUBLIC COMPONENT MODEL METHODS */
    /*******************************************/
    get renderer() {
        return this.getRenderer();
    }
    getRenderer() {
        return this._renderer;
    }
    shutdown() {
        this._unregisterListeners();
        let prom = this._renderer._shutdown();
        this._renderer = undefined;
        this._options = undefined;
        this._DEFAULT_PLACEHOLDER = undefined;
        this._placeholder = undefined;
        this._autoselect = undefined;
        this._dropUp = undefined;
        this._showFilter = undefined;
        this._disabled = undefined;
        this._invalid = undefined;
        this._visible = undefined;
        this._value = undefined;
        this._darkTheme = undefined;
        return prom;
    }
    /*******************************************/
    /* END COMPONENT MODEL METHODS */
    /* START HANDLER METHODS */
    /*******************************************/

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE METHODS */
    /*******************************************/

    /*******************************************/
    /* END PRIVATE METHODS */
    /* START PUBLIC API METHODS */
    /*******************************************/

    /**
     * @schema.methods.getMultiple.name getMultiple
     * @schema.methods.getMultiple.description Returns a boolean representing the multiple version of the combobox
     * @schema.methods.getMultiple.returns boolean
     */
    getMultiple() {
        return this._multiple;
    }
    get multiple() {
        return this.getMultiple();
    }

    /**
     * @schema.methods.setPlaceholder.name setPlaceholder
     * @schema.methods.setPlaceholder.description Sets the placeholder text and returns an instance of the control for chainability
     * @schema.methods.setPlaceholder.returns Instance of the control
     * @schema.methods.setPlaceholder.parameters.newVal.name newVal
     * @schema.methods.setPlaceholder.parameters.newVal.description The new placeholder text
     * @schema.methods.setPlaceholder.parameters.newVal.type string
     */
    setPlaceholder(newVal) {
        if (newVal === undefined || newVal === null || newVal === "") {
            this._placeholder = this._DEFAULT_PLACEHOLDER;
        } else {
            this._placeholder = newVal;
        }
        this.emit("Placeholder", this._placeholder);
        return this;
    }
    /**
     * @schema.methods.getPlaceholder.name getPlaceholder
     * @schema.methods.getPlaceholder.description Returns the placeholder text
     * @schema.methods.getPlaceholder.returns string
     */
    getPlaceholder() {
        return this._placeholder;
    }
    get placeholder() {
        return this.getPlaceholder();
    }
    set placeholder(newVal) {
        this.setPlaceholder(newVal);
    }

    /**
     * @schema.methods.setAutoselect.name setAutoselect
     * @schema.methods.setAutoselect.description If enabled, sets the first option of the control selected
     * @schema.methods.setAutoselect.returns Instance of the control
     * @schema.methods.setAutoselect.parameters.newVal.name newVal
     * @schema.methods.setAutoselect.parameters.newVal.description The new autoselect state
     * @schema.methods.setAutoselect.parameters.newVal.type boolean
     */
    setAutoselect(newVal) {
        this._autoselect = newVal;
        this.emit("Autoselect", this._autoselect);
        return this;
    }
    /**
     * @schema.methods.getDropUp.name getDropUp
     * @schema.methods.getDropUp.description Returns the dropUp setting for the control
     * @schema.methods.getDropUp.returns boolean
     */
    getAutoselect() {
        return this._autoselect;
    }
    get autoselect() {
        return this.getAutoselect();
    }
    set autoselect(newVal) {
        this.setAutoselect(newVal);
    }

    /**
     * @schema.methods.setDropUp.name setDropUp
     * @schema.methods.setDropUp.description Sets the control to display the options above or below it
     * @schema.methods.setDropUp.returns Instance of the control
     * @schema.methods.setDropUp.parameters.newVal.name newVal
     * @schema.methods.setDropUp.parameters.newVal.description The new state for displaying the options
     * @schema.methods.setDropUp.parameters.newVal.type boolean
     */
    setDropUp(newVal) {
        this._dropUp = newVal;
        this.emit("DropUp", this._dropUp);
        return this;
    }
    /**
     * @schema.methods.getDropUp.name getDropUp
     * @schema.methods.getDropUp.description Returns the dropUp setting for the control
     * @schema.methods.getDropUp.returns boolean
     */
    getDropUp() {
        return this._dropUp;
    }
    get dropUp() {
        return this.getDropUp();
    }
    set dropUp(newVal) {
        this.setDropUp(newVal);
    }

    /**
     * @schema.methods.setShowFilter.name setShowFilter
     * @schema.methods.setShowFilter.description Toggles the display of the filter when the options are shown
     * @schema.methods.setShowFilter.returns Instance of the control
     * @schema.methods.setShowFilter.parameters.newVal.name newVal
     * @schema.methods.setShowFilter.parameters.newVal.description The display state of the filter
     * @schema.methods.setShowFilter.parameters.newVal.type boolean
     */
    setShowFilter(newVal) {
        this._showFilter = newVal;
        this.emit("ShowFilter", this._showFilter);
        return this;
    }
    /**
     * @schema.methods.getShowFilter.name getShowFilter
     * @schema.methods.getShowFilter.description Returns the display state of the filter
     * @schema.methods.getShowFilter.returns boolean
     */
    getShowFilter() {
        return this._showFilter;
    }
    get showFilter() {
        return this.getShowFilter();
    }
    set showFilter(newVal) {
        this.setShowFilter(newVal);
    }

    /**
     * @schema.methods.setAllowClear.name setAllowClear
     * @schema.methods.setAllowClear.description Sets the control to allow for clearing of value
     * @schema.methods.setAllowClear.returns Instance of the control
     * @schema.methods.setAllowClear.parameters.newVal.name newVal
     * @schema.methods.setAllowClear.parameters.newVal.description If set the user may clear the value
     * @schema.methods.setAllowClear.parameters.newVal.type boolean
     */
    setAllowClear(newVal) {
        this.emit("AllowClear", newVal);
        this._allowClear = newVal;
        return this;
    }
    /**
     * @schema.methods.allowClear.name getAllowClear
     * @schema.methods.allowClear.description Returns the allow clear option
     * @schema.methods.allowClear.returns boolean
     */
    getAllowClear() {
        return this._allowClear;
    }
    get allowClear() {
        return this.getAllowClear();
    }
    set allowClear(newVal) {
        this.setAllowClear(newVal);
    }

    /*******************************************/
    /* END PUBLIC API METHODS */
    /*******************************************/
}
module.exports = OrgsComboBoxComponent;
